import React, { useState, useEffect } from 'react';
import DataSetSelector from '../components/shared/DataSetSelector.jsx';
import { useQuery } from '@apollo/client/react';
import { PRODUCT_DATA } from '../services/queries.js';
import { ModuleRegistry, AllEnterpriseModule } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import DatasetLayout from '../components/shared/DatasetLayout.jsx';
import ReportHeader from '../components/shared/ReportHeader.jsx';
import PublicationsListReadOnly from '../components/shared/PublicationsListReadOnly.jsx';
import OutlookGrid from '../components/grids/OutlookGrid.jsx';
import OutlookChart from '../components/charts/OutlookChart.jsx';
import OutlookKey from '../components/shared/OutlookKey.jsx';
import DataPaginationBanner from '../components/shared/DataPaginationBanner.jsx';
import { parse } from 'graphql';
import { trackEvent } from '../services/appInsights';
import { BATCH_ROW_LIMIT } from '@esai/shared/dataLimits';

// Register all Community features
ModuleRegistry.registerModules([AllEnterpriseModule]);

// OutlookId is now required for all ProductData queries
const Outlook = (options) => {
	console.log('Outlook component options:', options);

	const [data, setData] = useState([]);
	const [isValidSelection, setIsValidSelection] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [gridReady, setGridReady] = useState(true);
	const [chartReady, setChartReady] = useState(true);

	// Setup initial settings based on options.outlook
	const {
		me,
		outlookId
	} = options;

	let outlook = {
		...options.outlook,
		restrictFilters: {
			...options.outlook.restrictFilters,
		}
	};

	// 1. Extract the filters that can be used to restrict the data (e.g., for the selector component)

	const title = outlook?.title || 'ESAI Outlook';

	// 2. Extract the initial selected values for filters (e.g., for the selector component)
	const [selectedValues, setSelectedValues] = useState({
		products: outlook.defaultFilters.products || [],
		activities: outlook.defaultFilters.activities || [],
		countries: outlook.defaultFilters.countries || [],
	});

	// Whenever the outlookId changes, we should reset the selected values to the defaults defined in the new outlook configuration.  This ensures that when a user switches to a different outlook, they see the default data for that outlook rather than retaining selections from the previous outlook which may not be relevant.
	useEffect(() => {
		setSelectedValues({
			products: outlook.defaultFilters.products || [],
			activities: outlook.defaultFilters.activities || [],
			countries: outlook.defaultFilters.countries || [],
		});
	}, [outlookId]); // Reset selected values when outlookId changes

	const isValidOutlookId = typeof outlookId === 'string' && outlookId.trim().length > 0;

	const [parsedRegions, setParsedRegions] = useState({
		countries: [],
		subregions: [],
		raw: [],
	});
	const [parsedProducts, setParsedProducts] = useState({
		products: [],
		sectors: [],
		raw: [],
	});

	useEffect(() => {
		const parsedRegions = getParsedRegionCountrySubregion(selectedValues);
		const productInput = parseProductInput(selectedValues.products);
		setParsedRegions(parsedRegions);
		setParsedProducts(productInput);
		if (selectedValues.products.length > 0 && selectedValues.activities.length > 0 && selectedValues.countries.length > 0) {
			setIsValidSelection(true);
		} else {
			setIsValidSelection(false);
		}
	}, [selectedValues]);
	// 3. Extract the filters that we will allow the user to select from (e.g., for the selector component).
	// Intersect the outlook's restrictFilters with the user's dataAccess permissions so the dropdowns
	// only show options the user is licensed for.
	const filterOptionsSetup = () => {
		const productGroups = outlook?.restrictFilters?.productGroups || false;
		const products = outlook?.restrictFilters?.products || false;
		const activities = outlook?.restrictFilters?.activities || false;
		const countries = outlook?.restrictFilters?.countries || false;

		// Build sets of user-allowed products and activities from me.dataAccess
		const userDataAccess = me?.dataAccess || [];
		const userAllowedProducts = new Set(userDataAccess.map(p => p.product));
		// Normalize: dataAccess stores "REFINERY_INTAKE" but restrictFilters uses "REFINERY INTAKE"
		const normalizeActivity = (a) => a.toUpperCase().replace(/_/g, ' ');
		const userAllowedActivities = new Set(
			userDataAccess.flatMap(p => (p.activities || []).map(a => normalizeActivity(a.activity)))
		);

		let filters = {};
		if (productGroups) filters.productGroups = productGroups;

		if (products) {
			const intersected = userAllowedProducts.size > 0
				? products.filter(p => userAllowedProducts.has(p))
				: products;
			if (intersected.length > 0) filters.products = intersected;
		}

		if (activities) {
			const intersected = userAllowedActivities.size > 0
				? activities.filter(a => userAllowedActivities.has(normalizeActivity(a)))
				: activities;
			if (intersected.length > 0) filters.activities = intersected;
		}

		if (countries) filters.countries = countries;

		const regions = outlook?.restrictFilters?.regions || false;
		if (regions) filters.regions = regions;

		return filters;
	}

	const [restrictFilters, setRestrictFilters] = useState(filterOptionsSetup());

	useEffect(() => {
		setRestrictFilters(filterOptionsSetup());
	}, [outlookId]); // Reset restrict filters when outlookId changes

	useEffect(() => {
		// Validate outlookId
		if (!isValidOutlookId) {
			// eslint-disable-next-line no-console
			console.error('Outlook: outlookId is required and must be a non-empty string.');
		}
	}, [outlookId]);

	// Apollo useQuery for product data
	const { data: queryData, loading, error, fetchMore } = useQuery(PRODUCT_DATA, {
		variables: {
			outlookId: outlookId,
			product: parsedProducts,
			country: parsedRegions.countries,
			subregion: parsedRegions.subregions,
			activity: selectedValues.activities,
			limit: BATCH_ROW_LIMIT,
			continuationToken: null,
		},
		skip: !isValidSelection,
		fetchPolicy: 'cache-first',
		notifyOnNetworkStatusChange: true,
		onCompleted: (data, variables) => {
			console.log('Query completed:', data, variables);
			trackEvent('DatasetViewed', {
				outlookId,
				outlookTitle: title,
				products: JSON.stringify(selectedValues.products),
				activities: JSON.stringify(selectedValues.activities),
				countries: JSON.stringify(selectedValues.countries),
				productCount: String(selectedValues.products.length),
				activityCount: String(selectedValues.activities.length),
				countryCount: String(selectedValues.countries.length),
				resultCount: String(data?.productData?.data?.length || 0),
			});
		},
		onError: (error) => {
			console.error('Query error:', error);
		}
	});

	const productDataMeta = queryData?.productData?.metaData;
	const loadedRows = queryData?.productData?.data?.length ?? 0;

	const handleLoadMore = async () => {
		const continuationToken = productDataMeta?.continuationToken;
		if (!continuationToken || loadingMore) return;
		setLoadingMore(true);
		try {
			await fetchMore({
				variables: { continuationToken },
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.productData) return prev;
					const prevTotal = prev?.productData?.metaData?.totalCount;
					return {
						productData: {
							...fetchMoreResult.productData,
							metaData: {
								...fetchMoreResult.productData.metaData,
								totalCount: prevTotal ?? fetchMoreResult.productData.metaData?.totalCount,
							},
							data: [
								...(prev?.productData?.data || []),
								...(fetchMoreResult.productData.data || []),
							],
						},
					};
				},
			});
		} catch (err) {
			console.error('Load more failed:', err);
		} finally {
			setLoadingMore(false);
		}
	};

	useEffect(() => {
	}, [parsedProducts]);

	useEffect(() => {
		console.log('Product data query - loading:', loading, 'error:', error, 'data:', data);

		if (queryData && queryData.productData) {
			console.log('Product data query result:', queryData.productData);
			setData(queryData?.productData?.data || []);
		}
	}, [queryData, loading, error]);

	// When a new data payload arrives, the grid and chart need to re-render.
	// Flip both to "not ready" so the banner can indicate the refresh phase;
	// each child flips its own flag back to true once it has consumed the data.
	useEffect(() => {
		if (queryData?.productData?.data) {
			setGridReady(false);
			setChartReady(false);
		}
	}, [queryData?.productData?.data]);

	const bannerStatus = (loading || loadingMore)
		? 'loading'
		: !gridReady
			? 'grid-refreshing'
			: !chartReady
				? 'chart-refreshing'
				: 'ready';

	// Parse selected region-country-subregion values
	const getParsedRegionCountrySubregion = (filters) => {
		// Each value: REGION.COUNTRY or REGION.COUNTRY.SUBREGION
		const countries = new Set();
		const subregions = [];
		(filters.countries || []).forEach(val => {
			const parts = val.split('.');
			if (parts.length === 3) {
				// REGION.COUNTRY.SUBREGION
				// countries.add(parts[1]);
				subregions.push({ country: parts[1], subregion: parts[2] });
			} else if (parts.length === 2) {
				// REGION.COUNTRY
				// subregions.push({ country: parts[1], subregion: null });
				countries.add(parts[1]);
			}
		});
		return {
			countries: Array.from(countries),
			subregions,
			raw: filters.countries || [],
		};
	};

	const parseProductInput = (products) => {
		return (products || []).map(p => {
			let [product, sector] = p.split('-').map(s => s.trim());
			if (!sector) {
				sector = '';
			}
			return { product, sector };
		});
	};

	const handleSelectorChange = (newValues) => {
		// Only update if values actually changed
		if (JSON.stringify(newValues) !== JSON.stringify(selectedValues)) {
			setSelectedValues(newValues);
		}
	};

	if (!isValidOutlookId) {
		return (
			<div style={{ color: 'red', padding: '2rem' }}>
				<strong>Error:</strong> outlookId is required for ProductData queries. Please provide a valid outlookId.
			</div>
		);
	}

	const { shortDescription, overviewUrl, publicationsTag, publicationTags, selectorTitle } = outlook;

	// Intersect outlook's publication tags with user's publicationAccess permissions
	const allOutlookTags = publicationTags || (publicationsTag ? [publicationsTag] : []);
	const userPubAccess = me?.publicationAccess;
	const effectiveTags = userPubAccess
		? allOutlookTags.filter(tag => userPubAccess.includes(tag))
		: allOutlookTags;

	return (
		<div>

			<DatasetLayout>
				<div className="outlook-header">
					<ReportHeader title={title} description={shortDescription} overviewUrl={overviewUrl} />
					<div className="outlook-header__pubs">
						{effectiveTags.length > 0 && (
							<PublicationsListReadOnly tags={effectiveTags} />
						)}
					</div>
				</div>
				<DataSetSelector
					title={selectorTitle}
					selectedValues={selectedValues}
					restrictFilters={restrictFilters}
					onValidationChange={() => { return }}
					onChange={handleSelectorChange}
				// onValidationChange={setIsValidSelection}
				/>

				{isValidSelection ? (
					<>
						<div>
							<p>Columns are dynamically generated based on selected filters.  You can customize the view by adjusting the filters above and by drag and drop columns within the grid.  Right click to export the data.</p>
							<DataPaginationBanner
								status={bannerStatus}
								loadedRows={loadedRows}
								totalCount={productDataMeta?.totalCount}
								hasMoreResults={productDataMeta?.hasMoreResults}
								onLoadMore={handleLoadMore}
							/>
							<OutlookGrid
								data={data}
								countries={parsedRegions}
								selectedValues={selectedValues}
								products={parsedProducts}
								title={title}
								onDataRendered={() => setGridReady(true)}
							/>
						</div>
						<div style={{ height: '600px', width: '100%', marginTop: '20px' }}>
							<OutlookChart
								data={data}
								countries={parsedRegions}
								selectedValues={selectedValues}
								products={parsedProducts}
								title={title}
								onDataRendered={() => setChartReady(true)}
							/>
						</div>
					</>
				) : (
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							textAlign: 'center',
							minHeight: '240px',
							marginTop: '20px',
							padding: '2rem',
							color: '#6b7280',
							border: '1px dashed #d1d5db',
							borderRadius: '8px',
							background: '#fafafa',
						}}
					>
						<p style={{ margin: 0, fontSize: '1.05rem' }}>
							Select a country, product, and activity to view data.
						</p>
					</div>
				)}

				<OutlookKey />
			</DatasetLayout>
		</div>
	);
};

export default Outlook;
